// Firebase client wrapper for auth and Firestore operations
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import {
  getFirestore,
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy
} from 'firebase/firestore';
import { Application, User, AnalyticsData } from '../types';
import { firebaseConfig } from '../firebaseConfig';

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

const applicationsCollection = collection(db, 'applications');

export const signInUser = async (email: string, password: string): Promise<any> => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
};

export const getUserData = async (uid: string): Promise<User | null> => {
  try {
    const d = doc(db, 'users', uid);
    const snap = await getDoc(d);
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      uid: uid,
      email: data.email,
      displayName: data.displayName,
      role: data.role, // Role is fetched but not updated
      createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
      updatedAt: data.updatedAt ? data.updatedAt.toDate() : new Date()
    } as User;
  } catch (err) {
    console.error('getUserData error', err);
    return null;
  }
};

export const submitApplication = async (applicationData: any): Promise<string> => {
  const now = new Date();
  const docRef = await addDoc(applicationsCollection, {
    ...applicationData,
    submittedAt: now,
    updatedAt: now,
    status: applicationData.status || 'submitted',
    stage: applicationData.stage || 'applied',
    type: 'application'
  });
  return docRef.id;
};

export const getAllApplications = async (): Promise<Application[]> => {
  const q = query(applicationsCollection, orderBy('submittedAt', 'desc'));
  const snaps = await getDocs(q);
  const apps: Application[] = snaps.docs.map(docSnap => {
    const data: any = docSnap.data();
    return {
      id: docSnap.id,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      dateOfBirth: data.dateOfBirth || '',
      gender: data.gender || 'other',
      fatherName: data.fatherName || '',
      motherName: data.motherName || '',
      address: data.address || { street: '', village: '', block: '', district: '', state: '', pincode: '' },
      mobileNumber: data.mobileNumber || '',
      emailId: data.emailId || '',
      education: data.education || { tenth: { board: '', year: '', percentage: 0, school: '' } },
      documents: data.documents || { photo: '', class10Certificate: '', class12Certificate: '', sportsCertificates: [] },
      status: data.status || 'pending',
      submittedAt: data.submittedAt ? data.submittedAt.toDate() : new Date(),
      reviewedAt: data.reviewedAt ? data.reviewedAt.toDate() : undefined,
      reviewedBy: data.reviewedBy,
      reviewNotes: data.reviewNotes,
      updatedAt: data.updatedAt ? data.updatedAt.toDate() : new Date()
    } as Application;
  });
  return apps;
};

export const getApplication = async (id: string): Promise<Application | null> => {
  const d = doc(db, 'applications', id);
  const snap = await getDoc(d);
  if (!snap.exists()) return null;
  const data: any = snap.data();
  return {
    id: snap.id,
    firstName: data.firstName || '',
    lastName: data.lastName || '',
    dateOfBirth: data.dateOfBirth || '',
    gender: data.gender || 'other',
    fatherName: data.fatherName || '',
    motherName: data.motherName || '',
    address: data.address || { street: '', village: '', block: '', district: '', state: '', pincode: '' },
    mobileNumber: data.mobileNumber || '',
    emailId: data.emailId || '',
    education: data.education || { tenth: { board: '', year: '', percentage: 0, school: '' } },
    documents: data.documents || { photo: '', class10Certificate: '', class12Certificate: '', sportsCertificates: [] },
    status: data.status || 'pending',
    submittedAt: data.submittedAt ? data.submittedAt.toDate() : new Date(),
    reviewedAt: data.reviewedAt ? data.reviewedAt.toDate() : undefined,
    reviewedBy: data.reviewedBy,
    reviewNotes: data.reviewNotes,
    updatedAt: data.updatedAt ? data.updatedAt.toDate() : new Date()
  } as Application;
};

export const updateApplicationStatus = async (id: string, status: string, reviewNotes?: string, reviewedBy?: string, byRole?: string): Promise<void> => {
  const d = doc(db, 'applications', id);
  const updates: any = { status, reviewNotes, reviewedBy, byRole, reviewDate: new Date(), updatedAt: new Date() };
  await updateDoc(d, updates);
};

export const deleteApplication = async (id: string): Promise<void> => {
  const d = doc(db, 'applications', id);
  await deleteDoc(d);
};

export const getAnalyticsData = async (): Promise<AnalyticsData> => {
  const apps = await getAllApplications();
  const statusCounts = apps.reduce((acc, app) => { acc[app.status] = (acc[app.status] || 0) + 1; return acc; }, {} as Record<string, number>);
  const districtCounts = apps.reduce((acc, app) => { const district = app.address.district || 'Unknown'; acc[district] = (acc[district] || 0) + 1; return acc; }, {} as Record<string, number>);
  return {
    totalApplications: apps.length,
    pendingApplications: statusCounts.submitted || 0,
    approvedApplications: statusCounts.approved || 0,
    rejectedApplications: statusCounts.rejected || 0,
    applicationsThisMonth: 0,
    applicationsThisWeek: 0,
    applicationsByDistrict: districtCounts,
    applicationsByMonth: {}
  } as AnalyticsData;
};

const firebaseClient = {
  signInUser,
  getUserData,
  submitApplication,
  getAllApplications,
  getApplication,
  updateApplicationStatus,
  deleteApplication,
  getAnalyticsData
};

export default firebaseClient;
